import { useEffect, useRef, useState } from "react";
import { Button, Card, Group } from "@mantine/core";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { idGenerator } from "generate-custom-id";

import QuerySection from "./components/QuerySection/QuerySection";
import SubQuery from "./components/SubQuery/SubQuery";
import StickyScrollX from "./components/StickyScrollX/StickyScrollX";
import ConnectorLayer from "./connectors/ConnectorLayer";
import ConnectorAnchor from "./connectors/ConnectorAnchor";
import { useRuleQuery } from "./hooks/useRuleQuery";
import { useAllEntitiesQuery } from "./hooks/useAllEntitiesQuery";
import { useCanEdit } from "./hooks/useCanEdit";
import { usePostSubQueryMutation } from "./hooks/mutations";
import { useQueryBuilderDnd } from "./dnd/useQueryBuilderDnd";
import DragPreview from "./dnd/DragPreview";
import Loader from "../loader/loader";

/**
 * Shared QueryBuilder canvas. Mounted by RuleEditorPage,
 * AdvancedSearchPage, and SavedSearchViewPage (with `readOnly` for the
 * last one). Owns the rule/entity queries, the central DndContext, the
 * ConnectorLayer, and the cursor-following DragOverlay. Page chrome and
 * results tables belong to the consumer page.
 */
export default function QueryBuilder({ ruleId, readOnly = false }) {
  const { data: rule, isLoading: isRuleLoading } = useRuleQuery(ruleId);
  const { data: allEntitiesData, isLoading: isEntitiesLoading } =
    useAllEntitiesQuery();
  const canEdit = useCanEdit({ rule, readOnly });

  const { sensors, collisionDetection, onDragOver, onDragEnd, onDragCancel } =
    useQueryBuilderDnd(ruleId);
  const { mutate: postSubQuery } = usePostSubQueryMutation({ ruleId });

  // Tracks the currently-active draggable so the DragOverlay can render
  // a preview. Without a sized overlay element, dnd-kit's collision
  // detection has no rect to match against and drops never fire.
  const [activeDrag, setActiveDrag] = useState(null);

  const topRightConnId = useRef(idGenerator("example", 4)).current;

  // Safety net: sortable's live reflow re-renders the dragged chip and its
  // siblings frequently mid-gesture. If that ever causes the dragged node
  // to be remounted, the browser drops native pointer capture and dnd-kit
  // never fires onDragEnd/onDragCancel — leaving this DragOverlay preview
  // stuck on screen indefinitely. Force-clear it shortly after any pointer
  // release if dnd-kit's own cleanup hasn't already run.
  useEffect(() => {
    if (!activeDrag) return undefined;
    const clearStuckOverlay = () => {
      setTimeout(() => {
        setActiveDrag((current) => (current ? null : current));
      }, 150);
    };
    window.addEventListener("pointerup", clearStuckOverlay);
    window.addEventListener("pointercancel", clearStuckOverlay);
    return () => {
      window.removeEventListener("pointerup", clearStuckOverlay);
      window.removeEventListener("pointercancel", clearStuckOverlay);
    };
  }, [activeDrag]);

  if (isRuleLoading || isEntitiesLoading || !rule) {
    return <Loader height="40vh" />;
  }

  const topLevelQuery = rule?.queries?.[0];
  const hasSubQueries = !!topLevelQuery?.queries?.length;

  const headerRightSlot = canEdit && topLevelQuery ? (
    <Button
      variant="outline"
      size="xs"
      onClick={() => postSubQuery(topLevelQuery.id)}
    >
      Subquery
    </Button>
  ) : null;

  const handleDragStart = (event) => {
    setActiveDrag(event.active);
  };

  const wrappedDragEnd = (event) => {
    setActiveDrag(null);
    onDragEnd(event);
  };

  const wrappedDragCancel = (event) => {
    setActiveDrag(null);
    onDragCancel(event);
  };

  // When the top-level query has subqueries we render its canvas in a
  // bordered card with a right-side connection point, then the SubQuery
  // rows draw arrows from that point.
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={readOnly ? undefined : handleDragStart}
      onDragOver={readOnly ? undefined : onDragOver}
      onDragEnd={readOnly ? undefined : wrappedDragEnd}
      onDragCancel={readOnly ? undefined : wrappedDragCancel}
    >
      {/* Sizing model:
          - flex-basis 45vw + grow + NO shrink: one box fills the width;
            two boxes (main + subquery) grow to ~50/50 and fit on screen;
            a third box can't shrink them, so the row overflows.
          - max-content root (ConnectorLayer) + StickyScrollX then provide
            the horizontal scroll for 3+ boxes (incl. nested subqueries),
            and the connector SVG still covers the full scrolled width. */}
      <StickyScrollX>
        <ConnectorLayer>
          <Group align="flex-start" gap="xl" wrap="nowrap">
            <Card
              withBorder
              padding="md"
              radius="md"
              pos="relative"
              style={{ flex: "1 0 45vw" }}
            >
              <QuerySection
                ruleId={ruleId}
                query={topLevelQuery}
                allEntitiesData={allEntitiesData}
                canEdit={canEdit}
                headerRightSlot={headerRightSlot}
              />
              {hasSubQueries && (
                <ConnectorAnchor id={topRightConnId} side="right" />
              )}
            </Card>

            {/* Top-level subqueries are horizontal siblings of the main
                query (not a vertical stack), so all queries read as one
                left-to-right row. */}
            {hasSubQueries &&
              topLevelQuery.queries.map((sub) => (
                <SubQuery
                  key={sub.id}
                  ruleId={ruleId}
                  queryData={sub}
                  allEntitiesData={allEntitiesData}
                  canEdit={canEdit}
                  startPointId={topRightConnId}
                />
              ))}
          </Group>
        </ConnectorLayer>
      </StickyScrollX>

      {/* DragOverlay lives OUTSIDE the ConnectorLayer so the SVG overlay
          doesn't try to draw against the floating drag preview. */}
      <DragOverlay dropAnimation={null}>
        {activeDrag ? <DragPreview active={activeDrag} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
